
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Radio, MapPin } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

interface PartnerDashboardHeaderProps {
  partnerName: string;
  partnerId: string;
  partnerPhone?: string;
  partnerEmail?: string;
  partnerLocation?: string;
  autoRefresh?: boolean;
  onToggleAutoRefresh?: () => void;
  onRefresh?: () => void;
  lastUpdated?: Date | null;
}

const PartnerDashboardHeader: React.FC<PartnerDashboardHeaderProps> = ({
  partnerName,
  partnerId,
  partnerPhone,
  partnerEmail,
  partnerLocation,
  autoRefresh = false,
  onToggleAutoRefresh,
  onRefresh,
  lastUpdated,
}) => {
  const navigate = useNavigate();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Partner is always a "client" visitor (not an admin browsing partners)
    setIsClient(true);
  }, []);

  const handleLogout = () => {
    if (isClient) {
      sessionStorage.removeItem("partnerId");
      sessionStorage.removeItem("partnerName");
      toast({
        title: "Αποσυνδεθήκατε",
        description: "Έχετε αποσυνδεθεί επιτυχώς.",
      });
      navigate("/partner/login");
    } else {
      navigate("/admin/partners");
    }
  };

  const initials = partnerName
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const formatLastUpdated = (date: Date) => {
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 60) return `${diffSec}δ πριν`;
    return `${Math.floor(diffSec / 60)}λ πριν`;
  };

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav className="sticky top-0 z-50 bg-slate-900 border-b border-white/10 shadow-xl">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 gap-3">

          {/* Left: Identity */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow-lg">
              {initials}
            </div>
            <div className="hidden sm:block min-w-0">
              <p className="text-white font-semibold text-sm leading-none truncate">{partnerName}</p>
              {partnerLocation ? (
                <p className="text-white/50 text-xs mt-0.5 flex items-center gap-0.5 truncate">
                  <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                  {partnerLocation}
                </p>
              ) : (
                <p className="text-white/40 text-xs mt-0.5">Πίνακας Ελέγχου</p>
              )}
            </div>
            {autoRefresh && (
              <div className="flex items-center gap-1 bg-emerald-500/20 border border-emerald-400/30 rounded-full px-2 py-0.5 flex-shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-emerald-300 text-[10px] font-semibold">LIVE</span>
              </div>
            )}
          </div>

          {/* Center: Section links (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-0.5">
            {[
              { label: "Επισκόπηση", id: "partner-overview" },
              { label: "Pipeline", id: "partner-pipeline" },
              { label: "Leads", id: "partner-leads" },
            ].map(({ label, id }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {lastUpdated && (
              <span className="text-white/30 text-xs hidden lg:block mr-1">
                {formatLastUpdated(lastUpdated)}
              </span>
            )}

            {onToggleAutoRefresh && (
              <button
                onClick={onToggleAutoRefresh}
                title={autoRefresh ? "Απενεργοποίηση Live" : "Ενεργοποίηση Live"}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  autoRefresh
                    ? "bg-emerald-500/20 border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/30"
                    : "bg-white/10 border-white/20 text-white/50 hover:bg-white/20 hover:text-white"
                }`}
              >
                <Radio className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Live</span>
              </button>
            )}

            {onRefresh && (
              <button
                onClick={onRefresh}
                title="Ανανέωση"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 border border-white/20 text-white/50 hover:bg-white/20 hover:text-white transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Ανανέωση</span>
              </button>
            )}

            <button
              onClick={handleLogout}
              title={isClient ? "Αποσύνδεση" : "Επιστροφή"}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 border border-white/20 text-white/50 hover:bg-red-500/20 hover:border-red-400/30 hover:text-red-300 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{isClient ? "Αποσύνδεση" : "Επιστροφή"}</span>
            </button>
          </div>

        </div>
      </div>
    </nav>
  );
};

export default PartnerDashboardHeader;
